
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../App';
import { useFriends } from '../hooks/useFriends';

import UserCard from '../components/browse/UserCard';
import { AvatarDisplay } from '../utils/avatars';
import { Loader2, Users, Heart, Bell, Search } from 'lucide-react';
import { isBirthdayToday } from '../utils/zodiac';

import { supabase } from '../utils/supabaseClient';
import { awardPoints } from '../utils/pointsService';
import { useModal } from '../context/ModalContext';

export default function Friends() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, setCurrentUser, blockedIds } = useContext(UserContext);
  const { data, isLoading, refetch: refetchFriends } = useFriends(currentUser?.id, blockedIds);
  const { showAlert } = useModal();

  const friendships = data?.friendships || [];
  const pendingRequests = data?.pendingRequests || [];
  const events = data?.events || [];

  const [activeTab, setActiveTab] = useState(location.state?.tab || 'friends'); // 'friends' or 'requests'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFeedEntry, setSelectedFeedEntry] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const invalidateFriends = () => refetchFriends();

  const refetchUser = async () => {
    if (!currentUser?.id) return;
    try {
      const { data } = await supabase.from('profiles').select('*').eq('id', currentUser.id).single();
      if (data) setCurrentUser({ ...currentUser, profile: data });
    } catch (error) {
      console.error("Failed to refetch user:", error);
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      const { error } = await supabase.from('friendships').update({ status: 'accepted', accepted_at: new Date().toISOString() }).eq('id', requestId);
      if (error) throw error;

      showAlert('成功', "已接受好友邀請！");
      awardPoints('new_friend', requestId);
      await refetchUser();
      invalidateFriends();
    } catch (error) {
      console.error("接受好友失敗:", error);
      showAlert('錯誤', "網路錯誤，請稍後重試");
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      const { error } = await supabase.from('friendships').delete().eq('id', requestId);
      if (error) throw error;

      showAlert('提示', "已拒絕好友邀請");
      invalidateFriends();
    } catch (error) {
      console.error("拒絕好友失敗:", error);
      showAlert('錯誤', "網路錯誤，請稍後重試");
    }
  };

  const handleViewProfile = (friend) => {
    navigate(`/user/${friend._id}`);
  };

  // Calculate shared activities between current user and a friend
  const getSharedActivitiesCount = (friendId) => {
    if (!currentUser || !events.length) return 0;

    const userEventIds = events
      .filter(event => {
        const participants = event.participants || [];
        const organizerId = event.organizer_id || event.organizer?._id;
        return participants.includes(currentUser._id) || organizerId === currentUser._id;
      })
      .map(event => event._id);

    const sharedCount = events.filter(event => {
      const participants = event.participants || [];
      const organizerId = event.organizer_id || event.organizer?._id;
      const friendInEvent = participants.includes(friendId) || organizerId === friendId;
      return friendInEvent && userEventIds.includes(event._id);
    }).length;

    return sharedCount;
  };

  if (isLoading && friendships.length === 0 && pendingRequests.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
        <Loader2 style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite' }} />
        <span style={{ fontSize: '1.2rem', marginLeft: '12px' }}>載入好友中...</span>
      </div>
    );
  }

  // 計算統計數據
  const totalFriends = friendships.length;
  const pendingRequestsCount = pendingRequests.length;

  // 今日壽星好友
  const allFriendUsers = friendships.map(f => f.user._id === currentUser._id ? f.friend : f.user);
  const birthdayFriends = allFriendUsers.filter(f => f.profile?.birthday && isBirthdayToday(f.profile.birthday));


  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #eef4f9 0%, #f0faf9 15%, #f8fffe 40%, #ffffff 100%)',
      padding: '0 0 80px 0',
      width: '100%',
      overflowX: 'hidden'
    }}>
      {/* Gradient Header — indigo/violet to differentiate from Browse */}
      <div style={{
        background: 'linear-gradient(135deg, #4e8bca 0%, #6a9fd3 60%, #9cc2e6 100%)',
        padding: isMobile ? '28px 20px 32px' : '36px 32px 40px',
        borderRadius: '0 0 32px 32px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', top: '-30px', right: '-20px',
          width: '120px', height: '120px', borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)'
        }} />
        <div style={{
          position: 'absolute', bottom: '-40px', left: '30px',
          width: '80px', height: '80px', borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)'
        }} />
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          {/* Left: title + subtitle */}
          <div style={{ minWidth: '150px', flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <Users size={isMobile ? 24 : 28} style={{ color: 'rgba(255,255,255,0.9)' }} />
              <h1 style={{
                fontSize: isMobile ? '1.75rem' : '2rem',
                fontWeight: '800',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang TC', 'Microsoft JhengHei', sans-serif",
                color: '#ffffff',
                margin: 0,
                letterSpacing: '-0.02em',
                lineHeight: '1.2',
                textShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}>
                揪好友
              </h1>
            </div>
            <p style={{
              color: 'rgba(255,255,255,0.85)',
              fontSize: '0.875rem',
              margin: 0,
              fontWeight: '400',
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang TC', sans-serif",
              lineHeight: '1.5'
            }}>
              與你在意的人保持聯繫
            </p>
          </div>

          {/* Right: stats pills (Removed per request) */}
        </div>
      </div>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>

        {/* Tabs: 探索新朋友 / 好友邀請 / 我的好友 */}
        <div style={{
          display: 'flex', gap: '4px', marginTop: '16px',
          background: 'rgba(255,255,255,0.9)', borderRadius: '999px', padding: '4px',
          border: '1px solid rgba(78, 139, 202, 0.08)',
          boxShadow: '0 4px 16px rgba(78, 139, 202, 0.06)'
        }}>
          <button
            onClick={() => navigate('/browse')}
            style={{
              flex: 1, padding: '10px 0', borderRadius: '999px', border: 'none',
              background: 'transparent', color: '#6b7280',
              fontSize: '14px', fontWeight: '600', cursor: 'pointer'
            }}
          >
            探索新朋友
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            style={{
              flex: 1, padding: '10px 0', borderRadius: '999px', border: 'none',
              background: activeTab === 'requests' ? '#4e8bca' : 'transparent',
              color: activeTab === 'requests' ? 'white' : '#6b7280',
              fontSize: '14px', fontWeight: activeTab === 'requests' ? '700' : '600', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
            }}
          >
            好友邀請
            {pendingRequestsCount > 0 && (
              <span style={{
                background: activeTab === 'requests' ? 'rgba(255,255,255,0.2)' : 'rgba(78, 139, 202, 0.1)',
                color: activeTab === 'requests' ? 'white' : '#4e8bca',
                padding: '2px 6px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold'
              }}>
                {pendingRequestsCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('friends')}
            style={{
              flex: 1, padding: '10px 0', borderRadius: '999px', border: 'none',
              background: activeTab === 'friends' ? '#4e8bca' : 'transparent',
              color: activeTab === 'friends' ? 'white' : '#6b7280',
              fontSize: '14px', fontWeight: activeTab === 'friends' ? '700' : '600', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
            }}
          >
            我的好友
            <span style={{
              background: activeTab === 'friends' ? 'rgba(255,255,255,0.2)' : 'rgba(78, 139, 202, 0.1)',
              color: activeTab === 'friends' ? 'white' : '#4e8bca',
              padding: '2px 6px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold'
            }}>
              {totalFriends}
            </span>
          </button>
        </div>

        {/* Search Bar — above avatar strip */}
        <div style={{ position: 'relative', margin: '16px 0 8px', display: activeTab === 'friends' ? 'block' : 'none' }}>
          <Search style={{
            position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
            width: '18px', height: '18px', color: '#9ca3af'
          }} />
          <input
            type="text"
            placeholder="搜尋好友名稱、城市或職業..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%', paddingLeft: '44px', paddingRight: '16px',
              paddingTop: '12px', paddingBottom: '12px',
              borderRadius: '14px', border: '1px solid rgba(78, 139, 202, 0.08)',
              fontSize: '14px', outline: 'none',
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
              boxShadow: '0 4px 16px rgba(78, 139, 202, 0.06)',
              backdropFilter: 'blur(8px)', transition: 'all 0.2s'
            }}
            onFocus={(e) => { e.target.style.borderColor = '#4e8bca'; e.target.style.boxShadow = '0 4px 20px rgba(78, 139, 202, 0.15)'; }}
            onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)'; }}
          />
        </div>

        {/* Combined avatar strip: birthday friends */}
        {(birthdayFriends.length > 0 && activeTab === 'friends') && (
          <div style={{
            display: 'flex',
            gap: '14px',
            overflowX: 'auto',
            padding: '20px 0 16px',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
            alignItems: 'flex-start',   /* avatars align at top so expanded text doesn't shift others */
          }}>

            {/* Birthday friends */}
            {birthdayFriends.map((friend, idx) => {
              const key = `bday-${idx}`;
              const fullText = `今天生日🎂 記得送上祝福🥳`;
              const LIMIT = 8;
              const isLong = fullText.length > LIMIT;
              const isSelected = selectedFeedEntry?.key === key;
              const displayText = isSelected ? fullText : (isLong ? fullText.slice(0, LIMIT) + '…' : fullText);
              return (
                <div
                  key={key}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', flexShrink: 0, width: '64px', cursor: 'pointer' }}
                  onClick={() => setSelectedFeedEntry(isSelected ? null : { key, type: 'birthday', friend })}
                >
                  <div style={{
                    padding: '2.5px', borderRadius: '50%',
                    background: 'linear-gradient(135deg, #fb923c, #f97316)',
                    boxShadow: isSelected ? '0 0 0 3px #fed7aa' : '0 2px 8px rgba(249,115,22,0.4)',
                    position: 'relative', transition: 'box-shadow 0.2s',
                  }}>
                    <AvatarDisplay avatarId={friend.profile?.avatar || 'avatar1'} size={44} />
                    <div style={{
                      position: 'absolute', top: -4, right: -4,
                      width: '18px', height: '18px', borderRadius: '50%',
                      background: '#f97316', border: '2px solid white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px',
                    }}>🎂</div>
                  </div>

                  {/* Name */}
                  <span style={{ fontSize: '0.72rem', color: '#ea580c', fontWeight: '700', width: '100%', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {friend.profile?.name}
                  </span>

                  {/* Text — expands with border on click */}
                  <span style={{
                    fontSize: '0.63rem',
                    color: isSelected ? '#c2410c' : '#f97316',
                    fontWeight: '600',
                    width: '100%',
                    textAlign: 'center',
                    lineHeight: '1.4',
                    wordBreak: 'break-all',
                    ...(isSelected ? {
                      background: 'linear-gradient(135deg, #fff7ed, #ffedd5)',
                      border: '1.5px solid #fed7aa',
                      borderRadius: '8px',
                      padding: '5px 6px',
                    } : {}),
                  }}>
                    {displayText}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <div style={{ marginTop: '24px' }}>
            {pendingRequests.length > 0 ? (
              <div style={{
                display: 'grid',
                gap: '16px',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))'
              }}>
                {pendingRequests.map(request => (
                  <div 
                    key={request._id} 
                    onClick={() => handleViewProfile(request.user)}
                    style={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '16px',
                      padding: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.03)'; }}
                  >
                    <AvatarDisplay
                      avatarId={request.user.profile?.avatar || 'avatar1'}
                      size={50}
                    />
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        fontWeight: '600',
                        color: '#2d3748',
                        marginBottom: '4px'
                      }}>
                        {request.user.profile?.name || '未知用戶'}
                      </h4>
                      <p style={{
                        color: '#6b7280',
                        fontSize: '0.875rem',
                        margin: 0
                      }}>
                        想要與您成為好友
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        style={{
                          backgroundColor: '#059669',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 12px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAcceptRequest(request._id);
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#047857'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#059669'}
                      >
                        接受
                      </button>
                      <button
                        style={{
                          backgroundColor: '#f3f4f6',
                          color: '#4b5563',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '8px 12px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeclineRequest(request._id);
                        }}
                        onMouseEnter={(e) => { e.target.style.backgroundColor = '#fee2e2'; e.target.style.color = '#dc2626'; }}
                        onMouseLeave={(e) => { e.target.style.backgroundColor = '#f3f4f6'; e.target.style.color = '#4b5563'; }}
                      >
                        拒絕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                minHeight: '300px', backgroundColor: 'white', borderRadius: '24px', padding: '40px',
                textAlign: 'center', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <Bell style={{ width: '48px', height: '48px', color: '#d1d5db', marginBottom: '16px' }} />
                <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#374151', marginBottom: '8px' }}>目前沒有待處理的好友邀請</h3>
              </div>
            )}
          </div>
        )}

        {/* Friends Tab */}
        {activeTab === 'friends' && (() => {
          // Filter friendships based on search query
          const filteredFriendships = friendships.filter(friendship => {
            const friendUser = friendship.user._id === currentUser._id ? friendship.friend : friendship.user;
            const query = searchQuery.toLowerCase();
            const name = (friendUser.profile?.name || '').toLowerCase();
            const city = (friendUser.profile?.city || '').toLowerCase();
            const occupation = (friendUser.profile?.occupation || '').toLowerCase();

            return name.includes(query) || city.includes(query) || occupation.includes(query);
          });

          return filteredFriendships.length > 0 ? (
            <div style={{
              display: 'grid',
              gap: isMobile ? '16px' : '20px',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)'
            }}>
              {filteredFriendships.map(friendship => {
                // Determine which user in the friendship document is the actual friend
                const friendUser = friendship.user._id === currentUser._id ? friendship.friend : friendship.user;
                return (
                  <UserCard
                    key={friendship._id}
                    user={friendUser.profile}
                    friendshipStatus="accepted"
                    viewMode="list"
                    onViewProfile={() => handleViewProfile(friendUser)}
                    sharedActivitiesCount={getSharedActivitiesCount(friendUser._id)}
                    currentUserInterests={currentUser?.profile?.interests || []}
                  />
                );
              })}
            </div>
          ) : searchQuery ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '300px',
              backgroundColor: 'white',
              borderRadius: '24px',
              padding: '40px',
              textAlign: 'center',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Search style={{
                width: '48px',
                height: '48px',
                color: '#d1d5db',
                marginBottom: '16px'
              }} />
              <h3 style={{
                fontSize: '1.125rem',
                fontWeight: 'bold',
                color: '#374151',
                marginBottom: '8px'
              }}>
                找不到符合的好友
              </h3>
              <p style={{
                color: '#6b7280',
                marginBottom: '0'
              }}>
                試試搜尋其他關鍵字
              </p>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              backgroundColor: 'white',
              borderRadius: '24px',
              padding: '60px',
              textAlign: 'center',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Heart style={{
                width: '64px',
                height: '64px',
                color: '#d1d5db',
                marginBottom: '16px'
              }} />
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: '#374151',
                marginBottom: '8px'
              }}>
                還沒有好友
              </h3>
              <p style={{
                color: '#6b7280',
                marginBottom: '24px'
              }}>
                去探索頁面尋找志同道合的朋友吧！
              </p>
              <button
                onClick={() => navigate('/browse')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '16px 32px',
                  borderRadius: '20px',
                  border: 'none',
                  background: '#4e8bca',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: '0 8px 25px rgba(34, 197, 94, 0.3)',
                  letterSpacing: '0.02em'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#ef4444';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 35px rgba(34, 197, 94, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#4e8bca';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 25px rgba(34, 197, 94, 0.3)';
                }}
              >
                <Users style={{ width: '18px', height: '18px' }} />
                開始瀏覽用戶
              </button>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
